import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import t from "@/lang/lang";
import { GfsOffer } from "@/services/backend/gfs/offer/types";
import {
  useGfsOfferPdfDownloadMutation,
  useGfsOfferPdfPreviewQuery,
} from "@/services/backend/gfs/offer/api";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { SetGfsOfferImmutableButton } from "@/services/backend/gfs/offer/components/set-offer-immutable-button";

export function GfsOfferPdf({
  offer,
  onUpdated,
}: {
  offer: GfsOffer;
  onUpdated?: (offer: GfsOffer) => void;
}) {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
  const { data, isLoading, error } = useGfsOfferPdfPreviewQuery(
    { offerId: offer.id, download: false },
    { skip: !open },
  );

  const setSheetOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    setTooltipOpen(false);
    setTooltipSuppressed(!nextOpen);
  };

  return (
    <DocumentSheet open={open} onOpenChange={setSheetOpen}>
      <Tooltip
        open={tooltipOpen}
        onOpenChange={(nextOpen) => {
          if (open || tooltipSuppressed) {
            setTooltipOpen(false);
            return;
          }
          setTooltipOpen(nextOpen);
        }}
      >
        <DocumentSheetTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={() => {
                setTooltipOpen(false);
              }}
              onPointerEnter={() => setTooltipSuppressed(false)}
              onPointerLeave={() => {
                setTooltipOpen(false);
                setTooltipSuppressed(false);
              }}
              onBlur={() => {
                setTooltipOpen(false);
                setTooltipSuppressed(false);
              }}
            >
              PDF
            </Button>
          </TooltipTrigger>
        </DocumentSheetTrigger>
        <TooltipContent>
          <p>{t("PDF Vorschau / Herunterladen")}</p>
        </TooltipContent>
      </Tooltip>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Angebot")}
              documentNumber={
                <DocumentNumberById
                  documentId={offer.id}
                  skip={!offer.immutable}
                />
              }
            />
            <DocumentSheetActionsContent>
              <div className="mb-4 space-y-2">
                <div className="rounded-lg border p-4">
                  <p className="text-sm">
                    {offer.immutable
                      ? t("Dieses Angebot ist festgeschrieben.")
                      : t(
                          "Downloads aus einem bearbeitbaren Angebot enthalten das Wasserzeichen Vorschau.",
                        )}
                  </p>
                </div>
                <SetGfsOfferImmutableButton
                  offerId={offer.id}
                  immutable={offer.immutable}
                  triggerLabel={t("Angebot festschreiben")}
                  onUpdated={onUpdated}
                />
              </div>
              <DocumentSheetActionsDownload>
                <DownloadButton
                  offerId={offer.id}
                  download={false}
                  text={t("Vorschau herunterladen")}
                />
                <Separator />
                <FinalOfferDownloadButton offerId={offer.id} offer={offer} />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function FinalOfferDownloadButton({
  offerId,
  offer,
}: {
  offerId: string;
  offer: GfsOffer;
}) {
  if (offer.immutable) {
    return (
      <DownloadButton
        offerId={offerId}
        download
        text={t("Angebot herunterladen")}
      />
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block">
          <DownloadButton
            offerId={offerId}
            download
            text={t("Angebot herunterladen")}
            disabled
          />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {t(
          "Das Angebot kann erst heruntergeladen werden, wenn es festgeschrieben ist. Bis dahin können Sie die Vorschau herunterladen.",
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function DownloadButton({
  offerId,
  download,
  text,
  disabled = false,
}: {
  offerId: string;
  download: boolean;
  text: string;
  disabled?: boolean;
}) {
  const [downloadPdf, { isLoading, data, reset }] =
    useGfsOfferPdfDownloadMutation();

  useDownloadPdf(data, reset);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  return (
    <Button
      variant="outline"
      className="w-full justify-start"
      disabled={disabled || isLoading}
      onClick={() => downloadPdf({ offerId, download })}
    >
      <Download
        className={`mr-4 h-5 w-5 ${isLoading ? "animate-bounce" : ""}`}
      />
      {text}
    </Button>
  );
}
